/**
 * Report detail page.
 * 
 * @module pages/ReportDetailPage
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Building2,
  User,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, Badge, LoadingSection, ErrorState, EmptyState } from '../components/ui';
import {
  useReport,
  useReportResponses,
  useReportPromptMetrics,
  useAuditLog,
} from '../hooks';
import { AuditActionTypes, AuditResourceTypes } from '../lib/audit';
import { formatDateTime } from '../lib/utils';
import type { Response } from '../types';

/**
 * Response viewer component.
 */
function ResponseViewer({ response }: { response: Response }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {response.prompt_text}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {response.ai_model || 'Unknown model'} • {formatDateTime(response.created_at)}
          </p>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
        )}
      </button>
      {isExpanded && (
        <div className="p-4 border-t border-gray-200 space-y-4">
          {/* Raw text */}
          {response.raw_text && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Raw Response</h4>
              <pre className="text-sm bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                {response.raw_text}
              </pre>
            </div>
          )}
          {/* Parsed data */}
          {response.parsed_data && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Parsed Data</h4>
              <pre className="text-sm bg-gray-50 p-3 rounded-lg overflow-x-auto">
                {JSON.stringify(response.parsed_data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Report detail page component.
 * 
 * @returns Report detail page element
 */
export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { logAction } = useAuditLog();

  const { data: report, isLoading, error } = useReport(id);
  const { data: responses, isLoading: responsesLoading } = useReportResponses(id);
  const { data: promptMetrics } = useReportPromptMetrics(id);

  // Log page view
  useEffect(() => {
    if (report) {
      logAction(
        AuditActionTypes.VIEW_REPORT,
        AuditResourceTypes.REPORT,
        report.id,
        {
          business_name: report.businesses?.business_name,
          status: report.status,
        }
      );
    }
  }, [report, logAction]);

  // Log response views
  useEffect(() => {
    if (responses && responses.length > 0) {
      logAction(
        AuditActionTypes.VIEW_RESPONSES,
        AuditResourceTypes.RESPONSE,
        id,
        { count: responses.length }
      );
    }
  }, [responses, id, logAction]);

  if (isLoading) {
    return <LoadingSection text="Loading report details..." />;
  }

  if (error || !report) {
    return (
      <ErrorState
        message={error?.message || 'Report not found'}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const statusVariant =
    report.status === 'completed'
      ? 'success'
      : report.status === 'failed'
      ? 'error'
      : report.status === 'running'
      ? 'warning'
      : 'neutral';

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Reports
      </Link>

      {/* Report header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analysis Report</h1>
          <p className="text-gray-500 mt-1">
            {report.businesses?.business_name || 'Unknown Business'}
          </p>
        </div>
        <Badge variant={statusVariant} size="md">
          {report.status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report info */}
        <Card title="Report Information" className="lg:col-span-2">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Business
              </dt>
              <dd className="mt-1">
                <Link
                  to={`/businesses/${report.business_id}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {report.businesses?.business_name || report.business_id}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <User className="h-4 w-4" />
                Owner
              </dt>
              <dd className="mt-1">
                <Link
                  to={`/users/${report.profile_id}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {report.users?.email || report.profile_id}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Created
              </dt>
              <dd className="mt-1 text-gray-900">{formatDateTime(report.created_at)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Completed</dt>
              <dd className="mt-1 text-gray-900">
                {report.completed_at ? formatDateTime(report.completed_at) : '—'}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Metrics */}
        <Card title="Metrics">
          <dl className="space-y-4">
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">AI Visibility Score</dt>
              <dd className="text-2xl font-bold text-primary-600">
                {report.avg_ai_visibility_score !== null
                  ? `${(report.avg_ai_visibility_score * 100).toFixed(1)}%`
                  : '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">Total Mentions</dt>
              <dd className="text-2xl font-bold">{report.total_mentions ?? '—'}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-500">Responses</dt>
              <dd className="text-xl font-semibold">{responses?.length || 0}</dd>
            </div>
          </dl>
        </Card>
      </div>

      {/* Per-prompt metrics */}
      {promptMetrics && promptMetrics.length > 0 && (
        <Card title={`Prompt Metrics (${promptMetrics.length})`}>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Prompt</th>
                  <th>AI Score</th>
                  <th>Mentions</th>
                  <th>Sentiment</th>
                </tr>
              </thead>
              <tbody>
                {promptMetrics.map((metric) => (
                  <tr key={metric.id} className="hover:bg-gray-50">
                    <td className="max-w-md truncate">{metric.prompt_text}</td>
                    <td>
                      {metric.ai_visibility_score !== null
                        ? `${(metric.ai_visibility_score * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td>{metric.mention_frequency ?? '—'}</td>
                    <td>
                      {metric.sentiment ? (
                        <Badge
                          variant={
                            metric.sentiment === 'positive'
                              ? 'success'
                              : metric.sentiment === 'negative'
                              ? 'error'
                              : 'neutral'
                          }
                          size="sm"
                        >
                          {metric.sentiment}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Responses */}
      <Card title={`Individual Responses (${responses?.length || 0})`}>
        {responsesLoading ? (
          <LoadingSection text="Loading responses..." />
        ) : responses && responses.length > 0 ? (
          <div className="space-y-3">
            {responses.map((response) => (
              <ResponseViewer key={response.id} response={response} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="No responses"
            description="No AI responses have been recorded for this report."
          />
        )}
      </Card>
    </div>
  );
}
